import { Link } from 'react-router-dom';
import { GraduationCap, BookOpen, FileText, Compass, ClipboardText, ChatCircle, ArrowRight } from '@phosphor-icons/react';

const services = [
  {
    icon: GraduationCap,
    title: 'College Admissions',
    desc: 'We guide students through the college selection and admission process, matching them with institutions that fit their goals, budget, and qualifications.',
    details: ['Institution matching', 'Admission requirements review', 'Application strategy planning', 'Scholarship guidance'],
    image: 'https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=1600&auto=format&fit=crop',
  },
  {
    icon: Compass,
    title: 'Career & Education Pathway',
    desc: 'Our counselors help students explore career paths and choose programs that align with their long-term professional objectives.',
    details: ['Career assessment', 'Program recommendations', 'Industry alignment', 'Long-term planning'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1600&auto=format&fit=crop',
  },
  {
    icon: BookOpen,
    title: 'Exam Preparation Support',
    desc: 'Comprehensive preparation for iTEP, Accuplacer, CLBPT/CLBP, IELTS, and other admission-related exams.',
    details: ['Practice materials', 'Mock tests', 'Strategy coaching', 'Score improvement plans'],
    image: 'https://images.unsplash.com/photo-1513258496099-48168024aec0?q=80&w=1600&auto=format&fit=crop',
  },
  {
    icon: FileText,
    title: 'Application Assistance',
    desc: 'Full support for completing and submitting applications, including personal statements, essays, and required forms.',
    details: ['Application forms', 'Personal statements', 'Essay review', 'Submission tracking'],
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=1600&auto=format&fit=crop',
  },
  {
    icon: ClipboardText,
    title: 'Document Guidance',
    desc: 'We help students prepare, verify, and organize all required documents for a smooth application process.',
    details: ['Document checklist', 'Translation services', 'Credential evaluation', 'Notarization guidance'],
    image: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?q=80&w=1600&auto=format&fit=crop',
  },
  {
    icon: ChatCircle,
    title: 'Student Recruitment Support',
    desc: 'For recruiters and agents, we provide tools and support to submit students and manage applications efficiently.',
    details: ['Recruiter portal access', 'Student tracking', 'Commission management', 'Partnership support'],
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1600&auto=format&fit=crop',
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative py-24 bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=1920&auto=format&fit=crop"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-gray-50/80 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Our Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6" style={{ fontFamily: "'Libre Baskerville', serif" }}>
              Everything You Need<br /> for Admission Success
            </h1>
            <p className="text-base lg:text-lg text-gray-700 leading-relaxed">
              From college selection to exam preparation and document submission, we provide end-to-end support for students and recruitment partners.
            </p>
          </div>
        </div>
      </section>

      {/* Services with photos */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((s, i) => (
              <div key={s.title} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid={`service-${i}`}>
                {/* Image */}
                <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-sm">
                      <s.icon size={16} className="text-blue-600" weight="duotone" />
                      <span className="text-xs font-semibold text-gray-900">Service {String(i + 1).padStart(2, '0')}</span>
                    </div>
                  </div>
                </div>

                {/* Copy */}
                <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                      <s.icon size={24} className="text-blue-600" weight="duotone" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-widest">0{i + 1} / 0{services.length}</p>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.title}</h2>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6 text-base">{s.desc}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {s.details.map(d => (
                      <li key={d} className="flex items-start gap-2.5 text-sm text-gray-700">
                        <div className="shrink-0 w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center mt-0.5">
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                        </div>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Libre Baskerville', serif" }}>Ready to start your journey?</h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">Whether you're a student looking for admission support or a recruiter wanting to partner with us, we'd love to hear from you.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/apply" data-testid="services-apply-cta" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 px-6 py-3 rounded-md font-semibold transition-colors">
              Apply as Student <ArrowRight size={16} />
            </Link>
            <Link to="/recruiter-signup" data-testid="services-recruiter-cta" className="inline-flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-slate-700 px-6 py-3 rounded-md font-semibold transition-colors">
              Become a Recruiter <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
